import { makeEnum } from '../Base.js'
import { valOr } from '../SharedUtils.js'

/**
 * Enum for license types
 */
export const LicenseType = makeEnum({
  FreeTrial: 'Free Trial',
  PaidLicense: 'Paid License'
});

/**
 * Free trial configuration
 */
export const FreeTrialConfig = {
  TRIAL_DAYS: 30,
  TRIAL_MILLISECONDS: 30 * 24 * 60 * 60 * 1000, // 30 days in milliseconds
  INDEFINITE: 'indefinite' // Special value for indefinite trials
};

/**
 * Enum for license status
 */
export const LicenseStatus = makeEnum({
  Active: 'Active',
  Expired: 'Expired',
  Cancelled: 'Cancelled',
  Paused: 'Paused'
});

export const PlanName = makeEnum({
  Commercial: 'Commercial',
  Residential: 'Residential',
});

/**
 * Class to manage user license information
 */
export class License {
  constructor(opts) {
    opts = valOr(opts, {});
    this.type = valOr(opts.type, LicenseType.FreeTrial);
    this.status = valOr(opts.status, LicenseStatus.Active);
    // True when we have a subscription but the trial is still active.
    // Important for when they have subscribed to residential but still
    // want to access commercial projects via the trial.
    this.trialStillActive = valOr(opts.trialStillActive, false);
    this.expiresAt = valOr(opts.expiresAt, null);
    this.renewsAt = valOr(opts.renewsAt, null);
    this.planName = valOr(opts.planName, null);
    this.quantity = valOr(opts.quantity, 1);
    this.seatAssignments = valOr(opts.seatAssignments, []);
    this.debugOverride = valOr(opts.debugOverride, false);
  }

  /**
   * Create a license instance from Firebase data
   * @returns {License|null} License object or null if parsing fails
   */
  static fromBackendData(data) {
    try {
      if (!data) return null;

      // Find the best active one-time purchase up front, so we can use it as a
      // fallback when a subscription is inactive, or as the primary license when
      // there is no subscription at all.
      const activePurchase = this.getBestActivePurchase(data.purchases);

      // Handle the case where we have a subscription
      if (data.subscription) {
        const subscription = data.subscription;
        const subscriptionIsActive = subscription.status === 'active';

        // If the subscription is no longer active but there is a valid one-time
        // purchase, let the purchase grant access instead.
        if (!subscriptionIsActive && activePurchase) {
          return this.licenseFromPurchase(activePurchase, data);
        }

        let planName = '';
        if (subscription.planName) {
          planName = subscription.planName;
        } else if (subscription.productId) {
          planName = this.convertProductIdToPlanName(subscription.productId);
        } else {
          planName = PlanName.Commercial; // Default to Commercial if no plan name is provided
        }
        // Check for an overlapping trial
        let trialStillActive = false;
        if (data.trial && data.trial.expiresAt) {
          const now = new Date();
          const trialExpiryDate = new Date(data.trial.expiresAt);
          trialStillActive = now < trialExpiryDate;
        }
        return new License({
          type: LicenseType.PaidLicense,
          status: subscriptionIsActive ?
                  LicenseStatus.Active :
                  (subscription.status === 'paused' ?
                   LicenseStatus.Paused :
                   (subscription.status === 'cancelled' ?
                    LicenseStatus.Cancelled :
                    LicenseStatus.Expired)),
          trialStillActive: trialStillActive,
          planName: planName,
          quantity: subscription.quantity || 1,
          seatAssignments: subscription.seatAssignments || [],
          debugOverride: false
        });
      }

      // No subscription — check for an active one-time purchase next
      if (activePurchase) {
        return this.licenseFromPurchase(activePurchase, data);
      }

      if (data.trial) {
        // Handle trial data in the new schema
        const trial = data.trial;

        let type = LicenseType.FreeTrial;
        let isActive = null;
        if (trial.expiresAt === FreeTrialConfig.INDEFINITE) {
          isActive = true; // Indefinite trials are always active
        } else if (trial.expiresAt) {
          const now = new Date();
          const expiresAtDate = new Date(trial.expiresAt);
          isActive = now < expiresAtDate;
        } else {
          isActive = trial.status === 'Active';
        }

        return new License({
          type: type,
          status: isActive ? LicenseStatus.Active : LicenseStatus.Expired,
          expiresAt: trial.expiresAt,
          debugOverride: false
        });
      }

      // No valid license data found
      return null;
    } catch (error) {
      console.error("Error parsing license data:", error);
      return null;
    }
  }

  /**
   * Returns the best active one-time purchase from the purchases array:
   * - Filters out expired purchases (expiresAt <= now)
   * - Prefers Commercial over Residential (Commercial includes both plan types)
   * - Within the same type, prefers the latest expiry
   * Returns null if there are no active purchases.
   */
  static getBestActivePurchase(purchases) {
    if (!purchases || purchases.length === 0) return null;
    const now = Date.now();
    const active = purchases.filter(p => p.expiresAt > now);
    if (active.length === 0) return null;
    // Commercial access is a superset of Residential, so prefer it
    const commercial = active.filter(p => p.type === PlanName.Commercial);
    const pool = commercial.length > 0 ? commercial : active;
    return pool.reduce((best, p) => (p.expiresAt > best.expiresAt ? p : best));
  }

  /**
   * Build a License from a one-time purchase record.
   * Also checks whether the user has an overlapping trial that is still active.
   */
  static licenseFromPurchase(purchase, data) {
    let trialStillActive = false;
    if (data.trial && data.trial.expiresAt) {
      const now = new Date();
      const trialExpiryDate = new Date(data.trial.expiresAt);
      trialStillActive = now < trialExpiryDate;
    }
    return new License({
      type: LicenseType.PaidLicense,
      status: LicenseStatus.Active,
      planName: purchase.type, // 'Commercial' | 'Residential'
      expiresAt: purchase.expiresAt,
      trialStillActive: trialStillActive,
      debugOverride: false,
    });
  }

  static convertProductIdToPlanName(productId) {
    const productMap = {
      // HeatWise Commercial:
      '3561e773-73b3-4072-8f8f-8f107b4256b5': 'Commercial',
      // HeatWise Commercial (Annual):
      '0dfe499d-22cb-406d-8851-4d5fe5da7fce': 'Commercial',
      // HeatWise Residential:
      '0f97989b-6aa3-4788-ba33-28ac7b4c32fc': 'Residential',
      // HeatWise Commercial (Sandbox):
      '5614cce3-be66-4738-8b59-14379c0132f6': 'Commercial',
      // HeatWise Residential (Sandbox):
      'ee5f8894-7d51-4848-8684-9070e95e194d': 'Residential',
    };
    return productMap[productId] || PlanName.Commercial; // Default to Commercial if not found
  }

  /**
   * Load a user's license from Firestore
   * @param {Object} db - Firestore database instance
   * @param {string} userEmail - User's email address
   * @returns {Promise<License>} - Promise resolving to a License object
   */
  static async loadUserLicense(app, userEmail) {
    if (!userEmail) {
      console.log('No user email provided for license loading');
      return null; // Return null instead of free trial when no email is provided
    }

    try {
      // No need to pass email as the Cloud Function will get it from auth context.
      // reportErrors: false because this is called on a 5-second timer and errors
      // are handled by returning null — auto-reporting would flood Sentry on retry.
      const response = await app.fetchFromServer('get-user-license', 'GET', null, { reportErrors: false });
      
      if (response.error) {
        console.error("Error from getUserLicense function:", response.error);
        return null; // Return null instead of free trial on error
      }

      const license = License.fromBackendData(response.data);
      return license;
    } catch (error) {
      console.error("Error loading user license: ", error);
      return null; // Return null instead of free trial on exception
    }
  }

  /**
   * Create a free trial license
   * @param {boolean|string} indefinite - If true or 'indefinite', creates an indefinite trial
   * @returns {License} A free trial license
   */
  static createFreeTrial(indefinite = false) {
    return new License({
      type: LicenseType.FreeTrial,
      status: LicenseStatus.Active,
      expiresAt: indefinite === true || indefinite === FreeTrialConfig.INDEFINITE ? 
        FreeTrialConfig.INDEFINITE : // Special value for indefinite trials
        new Date(Date.now() + FreeTrialConfig.TRIAL_MILLISECONDS).toISOString(), // Trial period from now
    });
  }

  static createFreeTrialExpired() {
    return new License({
      type: LicenseType.FreeTrial,
      status: LicenseStatus.Expired,
      expiresAt: new Date(Date.now() - FreeTrialConfig.TRIAL_MILLISECONDS).toISOString(), // Trial period expired
    });
  }
  
  /**
   * Create a free trial license that is almost expired (2 days left)
   * @returns {License} A nearly expired free trial license
   */
  static createFreeTrialAlmostDone() {
    return new License({
      type: LicenseType.FreeTrial,
      status: LicenseStatus.Active,
      expiresAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days from now
    });
  }

  /**
   * Create a standard paid license
   * @param {Object} options - License options
   * @param {string} options.planName - Name of the plan
   * @param {number} options.quantity - Number of seats
   * @param {string} options.expiresAt - Expiration date ISO string
   * @param {string} options.renewsAt - Renewal date ISO string
   * @returns {License} A paid license
   */
  static createPaidLicense(options = {}) {
    return new License({
      type: LicenseType.PaidLicense,
      status: LicenseStatus.Active,
      planName: options.planName || PlanName.Commercial,
      quantity: options.quantity || 1,
      expiresAt: options.expiresAt || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
      renewsAt: options.renewsAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
    });
  }

  /**
   * Create an active one-time purchase license (expires in 1 month, no renewal)
   */
  static createOneTimePurchaseLicense(options = {}) {
    return new License({
      type: LicenseType.PaidLicense,
      status: LicenseStatus.Active,
      planName: options.planName || PlanName.Commercial,
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).getTime(), // 1 month from now (ms)
    });
  }

  /**
   * Create an expired one-time purchase license
   */
  static createExpiredOneTimePurchaseLicense(options = {}) {
    return new License({
      type: LicenseType.PaidLicense,
      status: LicenseStatus.Expired,
      planName: options.planName || PlanName.Commercial,
      expiresAt: new Date(Date.now() - 24 * 60 * 60 * 1000).getTime(), // 1 day ago (ms)
    });
  }

  /**
   * Create an expired license
   * @param {boolean} isPaid - Whether the license is paid or free trial
   * @returns {License} An expired license
   */
  static createExpiredLicense(isPaid = false) {
    return new License({
      type: isPaid ? LicenseType.PaidLicense : LicenseType.FreeTrial,
      status: LicenseStatus.Expired,
      expiresAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
      planName: isPaid ? PlanName.Commercial : null,
    });
  }

  /**
   * Create a cancelled license
   * @param {Object} options - License options
   * @param {string} options.planName - Name of the plan
   * @returns {License} A cancelled license
   */
  /**
   * Create an indefinite free trial license
   */
  static createIndefiniteFreeTrial() {
    return new License({
      type: LicenseType.FreeTrial,
      status: LicenseStatus.Active,
      expiresAt: FreeTrialConfig.INDEFINITE,
    });
  }

  static createCancelledLicense(options = {}) {
    return new License({
      type: LicenseType.PaidLicense,
      status: LicenseStatus.Cancelled,
      planName: options.planName || PlanName.Cancelled,
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
    });
  }

  /**
   * Create a team license with multiple seats
   * @param {number} seats - Number of seats
   * @returns {License} A team license
   */
  static createTeamLicense(seats = 5, ownerEmail = null) {
    // Create empty seat assignments - owner doesn't need to be assigned a seat
    const seatAssignments = Array(seats).fill('');
    
    return new License({
      type: LicenseType.PaidLicense,
      status: LicenseStatus.Active,
      planName: PlanName.Commercial,
      quantity: seats,
      expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
      renewsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      seatAssignments: seatAssignments
    });
  }
  
  /**
   * Update seat assignments for a team license
   * @param {Object} db - Firestore database instance
   * @param {string} userEmail - Owner's email address
   * @param {Array<string>} seatAssignments - Array of email addresses for seat assignments
   * @returns {Promise<boolean>} - Promise resolving to success status
   */
  async updateSeatAssignments(app, seatAssignments) {
    if (!this.isPaid() || this.quantity <= 1) {
      console.error('Cannot update seat assignments for non-team license');
      return false;
    }
    
    try {
      console.log('Updating seat assignments: ', seatAssignments);
      const response = await app.fetchFromServer('update-license-seats', 'POST', {
        seatAssignments: seatAssignments
      });
      
      if (response.error) {
        console.error("Error from updateLicenseSeats function: ", response.error);
        return false;
      }
      
      // Update the local license object
      this.seatAssignments = response.data.seatAssignments || seatAssignments;
      
      console.log('Seat assignments updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating seat assignments:', error);
      return false;
    }
  }

  isMultiSeatLicense() {
    return this.type === LicenseType.PaidLicense && this.quantity > 1;
  }

  /**
   * Check if the license is active
   */
  isActive() {
    return this.status === LicenseStatus.Active ||
            this.trialStillActive;
  }

  /**
   * Check if the license is a paid license
   */
  isPaid() {
    return this.type === LicenseType.PaidLicense;
  }

  /**
   * Check if the license is expired
   */
  isExpired() {
    if (this.status === LicenseStatus.Expired) return true;
    if (!this.expiresAt) return false;
    if (this.expiresAt === FreeTrialConfig.INDEFINITE) return false;
    
    const now = new Date();
    const expiryDate = new Date(this.expiresAt);
    return now > expiryDate;
  }

  /**
   * Get days remaining until expiration
   */
  getDaysRemaining() {
    if (!this.expiresAt) return null;
    if (this.expiresAt === FreeTrialConfig.INDEFINITE) return Infinity;
    
    const now = new Date();
    const expiryDate = new Date(this.expiresAt);
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  /**
   * Get the license name/type
   */
  getLicenseName() {
    if (this.planName) {
      return this.planName;
    }
    return 'Free Trial';
  }
  
  /**
   * Get the license status message
   */
  getLicenseStatus() {
    if (this.isExpired()) {
      if (this.type === LicenseType.FreeTrial) {
        return 'Your free trial has expired';
      }
      return 'Your license has expired';
    }
    
    if (this.status === LicenseStatus.Cancelled) {
      return 'Your subscription has been cancelled';
    }
    
    if (this.status === LicenseStatus.Paused) {
      return 'Your subscription is paused';
    }
    
    const daysRemaining = this.getDaysRemaining();
    if (daysRemaining !== null) {
      if (daysRemaining === Infinity || this.expiresAt === FreeTrialConfig.INDEFINITE) {
        return 'Unlimited access';
      }
      return `${daysRemaining} days remaining`;
    }
    
    return this.status;
  }
  
  residentialActive() {
    if (!this.isActive()) {
      return false;
    }
    if (this.type == LicenseType.FreeTrial || this.trialStillActive) {
      return true;
    }
    return (this.planName == PlanName.Residential || this.planName == PlanName.Commercial);
  }

  commercialActive() {
    if (!this.isActive()) {
      return false;
    }
    if (this.type == LicenseType.FreeTrial || this.trialStillActive) {
      return true;
    }
    return this.planName == PlanName.Commercial;
  }

  /**
   * Check if this license is equal to another license
   * @param {License} other - The other license to compare with
   * @returns {boolean} True if the licenses are equal
   */
  equals(other) {
    if (!other || !(other instanceof License)) {
      return false;
    }

    // Compare all relevant properties
    return (
      this.type === other.type &&
      this.status === other.status &&
      this.expiresAt === other.expiresAt &&
      this.renewsAt === other.renewsAt &&
      this.planName === other.planName &&
      this.trialStillActive === other.trialStillActive &&
      this.quantity === other.quantity &&
      this.seatAssignments.length === other.seatAssignments.length &&
      this.seatAssignments.every((seat, index) => seat === other.seatAssignments[index]) &&
      this.debugOverride === other.debugOverride
    );
  }
}
